/**
 * usePageParams — Drop-in replacement for react-router useParams()
 *
 * Works in both normal routing AND keep-alive tab rendering.
 * In keep-alive mode, the parent TabKeepAlive provides params via context
 * (because the URL may not match the tab's path when user is on another tab).
 */

import { createContext, useContext } from 'react';
import { useParams } from 'react-router-dom';

type Params = Record<string, string | undefined>;
type RenderedTab = { tabId: string; tabPath: string; isActive: boolean };

export const PageParamsContext = createContext<Params | null>(null);
export const RenderedTabContext = createContext<RenderedTab | null>(null);

/** Drop-in for useParams — context wins over router params in keep-alive */
export function usePageParams<
  T extends Record<string, string | undefined> = Record<string, string | undefined>,
>(): T {
  const ctx = useContext(PageParamsContext);
  const routerParams = useParams<T>();
  return (ctx !== null ? (ctx as unknown as T) : routerParams as unknown as T);
}

export function useRenderedTab() {
  return useContext(RenderedTabContext);
}
